import moment from 'moment';
import { useContext, useEffect, useState } from 'react';
import { useHttp, useNotification } from 'src/common/hooks';
import { Club } from 'src/common/types/clubs';
import { Room } from 'src/common/types/room';
import { AuthContext } from 'src/contexts';

export const ClubMeetingPopup = ({ club, onClose, onCreate }: ClubMeetingPopupProps) => {
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const { sendRequest: getRooms, isLoading: isGettingRooms } = useHttp();

  const notify = useNotification();
  const { accessToken, profile } = useContext(AuthContext);

  const {
    sendRequest: createMeeting,
    isLoading: creatingMeeting,
    error: errorCreatingMeeting,
  } = useHttp();

  useEffect(() => {
    if (errorCreatingMeeting) {
      notify(errorCreatingMeeting, 'error');
    }
  }, [errorCreatingMeeting]);

  useEffect(() => {
    getRoomsHandler();
  }, []);

  const getRoomsHandler = async () => {
    await getRooms(
      {
        url: 'events/rooms',
        method: 'GET',
      },
      (res: any) => {
        setRooms(res);
      },
    );
  };

  const createMeetingHandler = async () => {
    if (!selectedRoom) return;

    const startTime = moment().utc();
    const endTime = moment(startTime).add(10, 'minutes');

    const payload = {
      userId: profile?.id,
      clubId: club.id,
      currentTime: moment().utc().toISOString(),
      title: 'Club Meeting',
      description: 'This is a club meeting.',
      date: startTime.toDate(),
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      room: selectedRoom,
    };

    createMeeting(
      {
        url: 'events/club-meeting',
        method: 'POST',
        accessToken,
        payload,
      },
      (res: any) => {
        notify('Meeting Created.', 'success');

        if (res && res?.meetingLink) {
          setTimeout(() => {
            window.open(res.meetingLink, '_blank');
            onCreate();
            onClose();
          }, 1000);
        }
      },
    );
  };

  const renderRooms = () => {
    return rooms.map((room, index) => (
      <div
        key={index}
        className={`p-2.5 max-h-[110px] shadow-md border border-[#E5E5E5] rounded-lg flex ${
          selectedRoom === room ? 'bg-[#F3580005] border-[#F35800]' : ''
        }`}
      >
        <div className="relative w-1/2">
          <img
            src="/assets/placeholder-1.jpeg"
            alt="Placeholder"
            className="object-cover w-full h-full mb-5 rounded-lg"
          />

          <div
            className={`rounded-full text-white h-[34px] w-[34px] bg-[#F35800] flex items-center justify-center absolute top-0 bottom-0 my-auto -left-4 transition-opacity ${
              selectedRoom === room ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <svg
              width="10"
              height="8"
              viewBox="0 0 10 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 3.65789L4.08333 6.5L9 1.5"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>

        <div className="w-1/2 pl-4">
          <h3 className="mb-1 text-sm font-black leading-none text-black">
            {room?.roomName ?? 'Forum Room'}
          </h3>

          <p className="text-xs text-black text-opacity-70 mb-[15px]">
            Fits {room?.maxSeats} people
          </p>

          <button
            className={`text-[10px] border border-black p-[11px] rounded-[12px] font-[sohne-breit] font-medium leading-none ${
              selectedRoom === room ? 'text-[#F35800] !border-[#F35800]' : ''
            }`}
            onClick={() => setSelectedRoom(prevRoom => (prevRoom === room ? null : room))}
          >
            {selectedRoom === room ? 'Room is selected' : 'Select this room'}
          </button>
        </div>
      </div>
    ));
  };

  return (
    <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-[#0B1444E5] bg-opacity-90 z-50 flex-col p-10">
      <div className="w-[490px] relative bg-white rounded-lg p-14 shadow-lg flex flex-col overflow-y-scroll custom-scrollbar-1">
        <button className="absolute right-3 top-3" onClick={onClose}>
          <svg
            width="43"
            height="42"
            viewBox="0 0 43 42"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.5 15C0.5 6.71573 7.21573 0 15.5 0H27.5C35.7843 0 42.5 6.71573 42.5 15V27C42.5 35.2843 35.7843 42 27.5 42H15.5C7.21573 42 0.5 35.2843 0.5 27V15Z"
              fill="#E5E5E5"
            />
            <path d="M17.5 17L25.5 25" stroke="black" strokeWidth="2" strokeLinecap="round" />
            <path d="M25.5 17L17.5 25" stroke="black" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <h2 className="text-[34px] font-bold font-[sohne-breit] tracking-wide mb-5 leading-none">
          Start a club meeting in Room3d
        </h2>

        <p className="mb-5 leading-tight text-black text-md text-opacity-70">
          The power of reality and how people come together, collaborate, and communicate.
        </p>

        <div className="grid gap-5">
          <div className="grid gap-2.5 overflow-y-scroll max-h-[350px] custom-scrollbar">
            {renderRooms()}

            {isGettingRooms && !rooms.length && (
              <div role="status" className="mx-auto w-fit">
                <svg
                  aria-hidden="true"
                  className="inline w-10 h-10 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>

                <span className="sr-only">Loading...</span>
              </div>
            )}

            {!rooms.length && !isGettingRooms && (
              <div className={`text-center block p-8`}>No records found</div>
            )}
          </div>

          <div className="flex gap-6 mt-5">
            <button
              onClick={createMeetingHandler}
              disabled={creatingMeeting}
              className="px-8 py-[21px] text-xs text-center font-medium font-[sohne-breit] tracking-wider text-white bg-[#005AFF] rounded-lg focus:outline-none hover:bg-opacity-90 transition-opacity"
            >
              Suggest selected
            </button>

            <button onClick={onClose} className="text-black underline text-opacity-70">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ClubMeetingPopupProps {
  club: Club;
  onCreate: () => void;
  onClose: () => void;
}
